/**
 * WCC360 v2 — result composer.
 *
 * Turns the assessment state (Part 1 answers + Part 2 slot writes +
 * archetype tally) into the two things the result screen needs:
 *   1. A `Learner` object shaped for `matchCareers`.
 *   2. A short narrative sentence composed from the strongest signals.
 *
 * Nothing in here talks to the network. Match execution happens in the
 * page component so it can show a loading state while the bundle loads.
 */
import type {
  Archetype, ImpactDomain, Learner, MatchResult, StudyCapacity,
} from "@/lib/scoring";
import { ARCHETYPES, type ArchetypeVector, emptyVector } from "./archetypes";
import {
  effectiveSalaryFloorLpa, effectiveStudyCapacity, type Part1Answers,
} from "./types";
import { DEFAULT_BAND, type Band } from "./band";
import { emptySlots, slotsToLearnerPartial, type SlotState } from "./slots";
import { passionArchetypeNudge, passionFamilies, passionFamilyCounts } from "./passions";
import { MOBILITY_RESULT_NOTE, mobilityReach } from "./mobility";
import {
  CAUSE_NEXT_STEPS, ORDINAL, causeById, causeLearningPath, causeNextStep,
  type CauseMilestone, type CauseQuickWin, causeFamilyCounts, causeFamilyLabels,
  hobbyFamilyLabels, weightedPassionFamilyCounts, type CauseNextStep,
} from "./causes";
import {
  backgroundFamilyCounts, backgroundResultNote, nextStepNote, transferableSkills,
} from "./background";
import {
  dreamArchetypeNudge, pullArchetypeNudge, interestFamilyCounts, mergeFamilyCounts,
} from "./careerPull";
import {
  observerFamilies, observerFamilyCounts, observerFamilyLabels,
} from "./observer";
import { readApprovedObserverInput } from "./observerStore";
import { v1PortArchetypeNudge } from "./v1port/nudge";

// ---- study-capacity mapping ---------------------------------------------

const APPETITE_TO_STUDY: Record<string, StudyCapacity> = {
  none: "none",
  short_6mo: "short",
  part_time_1_2y: "parttime",
  full_degree: "degree",
};

/**
 * 15–18 band. Same four answers, same MIN rule — but school is still ahead
 * of them, so "3–5 years (a degree)" genuinely IS degree capacity, not the
 * part-time tier it would mean for a working adult. Mapping this ordinally
 * would filter every degree career out of a sixteen-year-old's results,
 * which looks plausible and is completely wrong.
 */
const APPETITE_TO_STUDY_15_18: Record<string, StudyCapacity> = {
  none: "none",             // Start working as soon as I can
  short_6mo: "parttime",    // 1–2 years (diploma or certificate)
  part_time_1_2y: "degree", // 3–5 years (a degree)
  full_degree: "degree",    // 5+ years (professional degree or research)
};

// ---- archetype ranking --------------------------------------------------

export function topArchetypes(tally: ArchetypeVector): {
  primary: Archetype; secondary: Archetype; ordered: Archetype[];
} {
  // Stable order — falls back to the canonical ARCHETYPES list on ties.
  const ordered = [...ARCHETYPES].sort((a, b) => {
    const d = (tally[b] ?? 0) - (tally[a] ?? 0);
    if (d !== 0) return d;
    return ARCHETYPES.indexOf(a) - ARCHETYPES.indexOf(b);
  });
  return { primary: ordered[0], secondary: ordered[1], ordered };
}

export const ARCHETYPE_TAGLINES: Record<Archetype, string> = {
  Builder:   "you want work you can point at when it's done",
  Thinker:   "you want to sit with hard problems until they crack",
  Creator:   "you want to make things that didn't exist yesterday",
  Connector: "you're at your best in the space between people",
  Organizer: "you make chaos into something that runs",
  Leader:    "you'd rather steer the ship than crew it",
  Caregiver: "you show up for people when it counts",
  Advocate:  "you want the work to change something that's wrong",
};

/** Same taglines, written about someone else (parent / third-person voice). */
export const ARCHETYPE_TAGLINES_THEY: Record<Archetype, string> = {
  Builder:   "they want work they can point at when it's done",
  Thinker:   "they like to sit with hard problems until they crack",
  Creator:   "they want to make things that didn't exist yesterday",
  Connector: "they're at their best in the space between people",
  Organizer: "they turn chaos into something that runs",
  Leader:    "they'd rather steer the ship than crew it",
  Caregiver: "they show up for people when it counts",
  Advocate:  "they want their work to change something that's wrong",
};

/** Capitalise the first letter, for taglines that start a sentence. */
export const sentenceCase = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** "a Builder" / "an Organizer". */
export const withArticle = (word: string) => `${/^[aeiou]/i.test(word) ? "an" : "a"} ${word}`;

// ---- assemble the Learner ------------------------------------------------

export interface AssessmentV2Snapshot {
  band?: Band;
  part1: Part1Answers;
  part2: Record<string, unknown>;
}

export function composeLearner(state: AssessmentV2Snapshot): Learner {
  const band = state.band ?? DEFAULT_BAND;
  const teen = band === "15to18";
  const explorer = band === "12to14";
  const slots = (state.part2.slots as SlotState) ?? emptySlots();
  const revealed = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
  // Passions nudge the tally, they do not set it. The nudge is normalised
  // to at most MAX_ARCHETYPE_NUDGE points across every pick, so it can
  // break a near-tie and nothing more — a stated interest must never
  // outrank twenty-two revealed-preference decisions. See passions.ts.
  // Same bounded channel for the "Your career matches" chapter: the
  // childhood dream is worth half a passion pick, the pull picks are worth
  // one. All three together stay well under a single Part 2 decision.
  const nudge = passionArchetypeNudge(state.part1.passion_ids);
  const dream = dreamArchetypeNudge(state.part1.childhood_dream);
  const pull = pullArchetypeNudge(state.part1.career_pull_ids);
  // The ported v1 instrument (46 trait statements + the three Nibble quizzes)
  // enters through the same bounded channel, capped at MAX_V1_NUDGE across all
  // 92 items and scaled down by how much of it was answered. Self-report can
  // break a near-tie; it can never manufacture a primary archetype. See
  // v1port/nudge.ts.
  const ported = v1PortArchetypeNudge(state.part2);
  const tally = { ...revealed } as ArchetypeVector;
  for (const a of ARCHETYPES) {
    tally[a] = (tally[a] ?? 0) + (nudge[a] ?? 0) + (dream[a] ?? 0)
      + (pull[a] ?? 0) + (ported[a] ?? 0);
  }
  const { primary, secondary } = topArchetypes(tally);

  const domains = state.part2.p2_whose_problem as
    | { first: ImpactDomain; second: ImpactDomain } | undefined;

  // X-OBSERVER-001. Approved-only, and resolved here so every surface that
  // scores a run — result page, dossier, snapshot backfill, replay — reads
  // the same bounded input.
  const observer = state.part1.observer_input ?? readApprovedObserverInput();

  const appetite = effectiveStudyCapacity(state.part1, band);
  const studyCapacity: StudyCapacity = explorer
    // 12–14: every path is still in front of them. We asked for nothing in
    // Part 1, so we filter on nothing — a made-up capacity here would
    // silently delete whole worlds of work from a twelve-year-old's result.
    ? "degree"
    : (teen ? APPETITE_TO_STUDY_15_18 : APPETITE_TO_STUDY)[appetite ?? "none"] ?? "none";

  // No salary floor below 18. We never asked for an absolute number, so we
  // must not invent one — filtering on a made-up floor would silently
  // delete whole families of work.
  const salaryFloorLpa =
    teen || explorer ? 0 : (effectiveSalaryFloorLpa(state.part1) ?? 0);

  // First-person affinity. Computed before the observer channel so the
  // observer can only ever add families the learner did not name.
  const firstPersonStrength = firstPersonFamilyStrength(state, band);

  return {
    ...slotsToLearnerPartial(slots),
    archetypePrimary: primary,
    archetypeSecondary: secondary,
    impactDomain1: domains?.first,
    impactDomain2: domains?.second,
    salaryFloorLpa,
    studyCapacity,
    // X-MOBILITY-001. Bounded adjustment only, never an eligibility filter.
    // Absent for 12–14 (we never ask them) so nothing is inferred.
    studyMobilityReach: explorer ? undefined : mobilityReach(state.part1.study_mobility),
    holdsDegree: teen || explorer
      ? false
      : state.part1.qualification === "bachelors" ||
        state.part1.qualification === "masters",
    yearsExperience: teen || explorer ? 0 : (state.part1.years_experience ?? 0),
    // Affinity only. Never read by the eligibility gate.
    passionFamilies: new Set([
      ...passionFamilies(state.part1.passion_ids),
      ...interestFamilyCounts(state.part1.childhood_dream, state.part1.career_pull_ids).keys(),
      // X-CAUSE-001 — a ranked cause counts even if they never tapped it on
      // the passions screen. Priorities should shape what they see.
      ...causeFamilyCounts(state.part1.cause_rank_ids).keys(),
      // X-BACKGROUND-001 — present only when they want to stay in or near
      // the field. A field someone wants out of is never boosted.
      ...backgroundFamilyCounts(state.part1.professional_field, state.part1.career_intent).keys(),
      // X-OBSERVER-001 — approved observations widen the pool of work on show.
      // They never gate it: no eligibility check reads this set.
      ...observerFamilies(observer, firstPersonStrength),
    ]),
    passionFamilyStrength: mergeFamilyCounts(
      firstPersonStrength,
      // X-OBSERVER-001 — half weight, hard-capped, and only for families the
      // learner did not already name themselves. Corroboration must not
      // double-count, or agreement becomes the loudest signal in the model.
      observerFamilyCounts(observer, firstPersonStrength),
    ),

  };
}


// ---- narrative headline -------------------------------------------------

/** Signals we can pull to compose the opening sentence. Every branch has
 *  a graceful fallback so we never render `undefined`. */
export function composeHeadline(L: Learner, tally: ArchetypeVector): string {
  const primary = L.archetypePrimary;
  const primaryLine = archetypeSentenceFragment(primary);

  // Furthest-from-neutral context preference.
  const contextCandidates: Array<[string, number]> = [
    ["moving, not at a desk",       L.prefMovement],
    ["outdoors when you can be",    L.prefOutdoor],
    ["running your own work",       L.prefAutonomy],
    ["a predictable working week",  L.prefStableSchedule],
    ["a blank page to work from",   L.prefCreativity],
  ];
  const contextPick = contextCandidates
    .map(([label, v]) => ({ label, drift: Math.abs((v ?? 50) - 50) }))
    .sort((a, b) => b.drift - a.drift)[0];

  const contextClause =
    contextPick && contextPick.drift >= 25
      ? `who needs ${contextPick.label}`
      : null;

  // Trade-off winner — impact/pay tradeoff.
  let tradeClause: string | null = null;
  if (L.prefIncomeUpside <= 35 && L.prefImpactVisibility >= 65) {
    tradeClause = "and you'd take a pay cut for work that matters";
  } else if (L.prefIncomeUpside >= 70) {
    tradeClause = "and you want the income upside";
  } else if (L.prefStability >= 70) {
    tradeClause = "and you want stability first";
  }

  const parts = [primaryLine];
  if (contextClause) parts.push(contextClause);
  if (tradeClause) parts.push(tradeClause);

  void tally;
  return joinNaturally(parts);
}

function archetypeSentenceFragment(a: Archetype): string {
  const map: Record<Archetype, string> = {
    Builder:   "You're a Builder",
    Thinker:   "You're a Thinker",
    Creator:   "You're a Creator",
    Connector: "You're a Connector",
    Organizer: "You're an Organizer",
    Leader:    "You're a Leader",
    Caregiver: "You're a Caregiver",
    Advocate:  "You're an Advocate",
  };
  return map[a];
}

function joinNaturally(parts: string[]): string {
  if (parts.length === 1) return parts[0] + ".";
  if (parts.length === 2) return `${parts[0]} ${parts[1]}.`;
  return `${parts[0]} ${parts[1]}, ${parts[2]}.`;
}

// ---- per-card "why it matched" -------------------------------------------

export const CONTEXT_LABELS: Record<string, { fit: string; miss: string }> = {
  outdoor:  { fit: "the day-to-day suits how you work — outdoors or on the move",
              miss: "it's more indoor than you'd like" },
  movement: { fit: "you'd actually be moving, not stuck at a desk",
              miss: "it's more desk-bound than you said you'd want" },
  screen:   { fit: "the screen-time load matches what you're comfortable with",
              miss: "it's more screen time than you said you'd want" },
  travel:   { fit: "the travel load fits what you said you'd take on",
              miss: "the travel is heavier than you said you'd want" },
  schedule: { fit: "the schedule shape matches what you'd choose",
              miss: "the schedule is less predictable than you said you'd want" },
  speaking: { fit: "the public-speaking demand is inside your comfort zone",
              miss: "you'd be presenting more than you said you have the energy for" },
  people:   { fit: "the people-contact load fits how you recharge",
              miss: "this role needs more sustained people contact than you said you have the energy for" },
  physical: { fit: "the physical closeness is inside what you're comfortable with",
              miss: "the physical closeness is more than you said you'd want" },
  sensory:  { fit: "the sensory exposure is inside your tolerance",
              miss: "the sensory exposure is heavier than you said you'd want" },
};

export const VALUES_LABELS: Record<string, { fit: string; miss: string }> = {
  autonomy:   { fit: "you'd get to run your own work",
                miss: "there's more oversight than you'd want" },
  impact:     { fit: "you'd see who you helped",
                miss: "your impact would be one step removed" },
  prestige:   { fit: "the recognition matches what you said mattered",
                miss: "it's quieter work than you said you'd want" },
  stability:  { fit: "it sits in the stable band you asked for",
                miss: "it's less stable than you said you'd want" },
  creativity: { fit: "the creative room matches what you'd choose",
                miss: "it's more prescribed than you said you'd want" },
  focus:      { fit: "the cognitive rhythm suits how you focus",
                miss: "it's more (or less) switching than you'd choose" },
  soloTeam:   { fit: "the solo/team balance matches your energy",
                miss: "it's the wrong side of solo-vs-team for you" },
  socialLoad: { fit: "the social load is inside what you can sustain",
                miss: "the social load is heavier than you said you can carry" },
  emotional:  { fit: "the emotional load is inside what you can carry",
                miss: "the emotional load is heavier than you said you can carry" },
  grind:      { fit: "the grunt-work share is inside what you'd tolerate",
                miss: "there's more repetitive/admin work than you said you'd tolerate" },
};

export interface WhyItMatched {
  fits: string[];      // 2 strongest
  watch: string | null; // 1 weakest — if genuinely weak
}

/**
 * Explainability context for "Why you matched".
 *
 * The affinity bonus is deliberately small, but it is the part of the score
 * a learner recognises as *their own words* — so when a ranked cause or a
 * live hobby is what surfaced a career, we name it, in their language, first.
 * Build it once per result with `explainContext` and pass it to every card.
 */
export interface MatchExplainContext {
  causes: Map<string, { label: string; ordinal: string }>;
  hobbies: Map<string, { label: string; phrase: string }>;
  /** X-OBSERVER-001 — named honestly: they noticed, you confirmed. */
  observed: Map<string, { label: string; noun: string }>;
}


/**
 * The learner's own family affinity, before the observer channel touches it.
 *
 * Exported because the observer bound — "add nothing to a family they already
 * named" — is only checkable against this exact map. Any surface that wants to
 * show what an observation *did* must compare against the same baseline the
 * engine used, or the audit lies.
 */
export function firstPersonFamilyStrength(
  state: AssessmentV2Snapshot,
  band?: Band,
): Map<string, number> {
  const b = band ?? parseBandFromState(state);
  const explorer = b === "12to14";
  const teen = b === "15to18";
  return mergeFamilyCounts(
    // X-HOBBY-002 — the same picks, scaled by how often they really do the
    // thing and how highly they ranked the cause. Bounded multiplier only.
    weightedPassionFamilyCounts(state.part1.passion_ids, state.part1),
    causeFamilyCounts(state.part1.cause_rank_ids),
    interestFamilyCounts(state.part1.childhood_dream, state.part1.career_pull_ids),
    explorer || teen
      ? new Map<string, number>()
      : backgroundFamilyCounts(state.part1.professional_field, state.part1.career_intent),
  );
}

function parseBandFromState(state: AssessmentV2Snapshot): Band {
  return (state.band as Band | undefined) ?? DEFAULT_BAND;
}

export function explainContext(state: AssessmentV2Snapshot): MatchExplainContext {
  const observer = state.part1.observer_input ?? readApprovedObserverInput();
  const firstPerson = firstPersonFamilyStrength(state);
  return {
    causes: causeFamilyLabels(state.part1.cause_rank_ids),
    hobbies: hobbyFamilyLabels(state.part1.passion_ids, state.part1.hobby_frequency),
    observed: observerFamilyLabels(observer, firstPerson),
  };
}

export function whyItMatched(r: MatchResult, ctx?: MatchExplainContext): WhyItMatched {
  const all: { key: string; score: number; block: "context" | "values" }[] = [
    ...Object.entries(r.contextDetail).map(([k, v]) => ({ key: k, score: v, block: "context" as const })),
    ...Object.entries(r.valuesDetail).map(([k, v]) => ({ key: k, score: v, block: "values" as const })),
  ];
  const sorted = [...all].sort((a, b) => b.score - a.score);
  const top = sorted.slice(0, 2);
  const bottom = sorted[sorted.length - 1];

  const fits = top
    .map((s) => (s.block === "context" ? CONTEXT_LABELS[s.key]?.fit : VALUES_LABELS[s.key]?.fit))
    .filter(Boolean) as string[];

  // Passions lead the explanation when they applied. They're a small part of
  // the score, but they're the part a learner recognises as their own words —
  // so we say it first, and we say it plainly. X-CAUSE-001 and X-HOBBY-002
  // are more specific than "you're drawn to this", so they win the lead.
  const cause = ctx?.causes.get(r.family);
  const hobby = ctx?.hobbies.get(r.family);
  if (r.passion > 0) {
    if (cause) {
      fits.unshift(
        `this is what working on ${cause.label.toLowerCase()} actually looks like as a job — you ranked it your ${cause.ordinal} priority`,
      );
    }
    if (hobby) {
      fits.unshift(
        `you already do ${hobby.label.toLowerCase()} ${hobby.phrase}, and this is that same instinct, paid for`,
      );
    }
    if (!cause && !hobby) {
      const observed = ctx?.observed.get(r.family);
      if (observed) {
        // X-OBSERVER-001. Never presented as the learner's own words, and
        // never without the approval — that consent is the whole licence for
        // this line existing.
        fits.unshift(
          `${observed.noun} noticed ${observed.label.toLowerCase()} in you and you agreed — this is where that goes`,
        );
      } else {
        fits.unshift(`it's ${r.family} work, which you told us you're drawn to`);
      }
    }
  }


  // X-MOBILITY-001. Only spoken about when it actually moved the score.
  if (r.mobility > 0) {
    fits.push("the travel and location demands fit how far you said you could go");
  }


  const watch =
    r.mobility < 0
      ? "it usually means travelling or moving for the work, which you said isn't possible right now"
      : bottom && bottom.score < 55
      ? (bottom.block === "context"
          ? CONTEXT_LABELS[bottom.key]?.miss
          : VALUES_LABELS[bottom.key]?.miss) ?? null
      : null;

  return { fits, watch };
}

/**
 * X-MOBILITY-001 — the one-line explanation for the result screen. Null for
 * the 12–14 band, which is never asked the question.
 */
export function mobilityResultNote(state: AssessmentV2Snapshot): string | null {
  if ((state.band ?? DEFAULT_BAND) === "12to14") return null;
  const m = state.part1.study_mobility;
  return m ? MOBILITY_RESULT_NOTE[m] : null;
}

/**
 * X-BACKGROUND-001 — the professional-history block for the result screen.
 * Null for anyone we never asked (both younger bands, and any adult with no
 * paid experience).
 */
export interface BackgroundBlock {
  note: string;
  transferables: string[];
  nextStep: string | null;
}

export function backgroundBlock(
  state: AssessmentV2Snapshot,
  topMatchName: string | null = null,
): BackgroundBlock | null {
  if ((state.band ?? DEFAULT_BAND) !== "18plus") return null;
  const { professional_field: field, career_intent: intent, years_experience: years } = state.part1;
  const note = backgroundResultNote(field, intent, years);
  if (!note) return null;
  return {
    note,
    // Only worth showing when the point is movement.
    transferables: intent === "switch" || intent === "adjacent" ? transferableSkills(field) : [],
    nextStep: nextStepNote(field, intent, topMatchName),
  };
}

// ---- distance badge → colour class --------------------------------------

export const DISTANCE_STYLES: Record<string, string> = {
  "Lateral move":         "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Short reskill":        "bg-sky-500/15 text-sky-400 border-sky-500/30",
  "Retrain 1-2 yrs":      "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "Full requalification": "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

export function formatSalary(lo: number | null, hi: number | null): string {
  const l = lo && lo > 0 ? lo : null;
  const h = hi && hi > 0 ? hi : null;
  if (l == null && h == null) return "No salary data yet";
  if (l != null && h != null && l !== h) return `₹${l}–${h} LPA`;
  const v = (l ?? h)!;
  return `₹${v} LPA`;
}

// ---- X-CAUSE-001: causes, and what to do about them ---------------------

export interface CauseStepBlock {
  id: string;
  label: string;
  /** "first", "second", "third" — their own stated order. */
  ordinal: string;
  /** Scroll-stopper, one line, spoken. */
  hook: string;
  why: string;
  /** The reference they've already seen, so this doesn't feel foreign. */
  culture: string;
  /** 3–5 milestones, each with a checkpoint you could show someone. */
  milestones: CauseMilestone[];
  /** The under-30-minute doorway, doable this week. */
  quickWin: CauseQuickWin;
  /** Flat string view, for the dossier and the PDF. */
  learningPath: string[];
  firstAction: string;
  /** The strongest match in this result that sits in one of the cause's
   *  families, so the route lands on a real job title, not an abstraction. */
  matchTitle: string | null;
}

/**
 * The cause-led next steps. One block per ranked cause, in the learner's own
 * order of priority. Each carries a hook, a cultural anchor, 3–5 milestones
 * with real checkpoints, a 30-minute quick win for this week, and one bigger
 * action for this month.
 *
 * Returns an empty array for a learner who told us no single cause stands
 * out — we honour that rather than inventing a priority for them.
 */
export function causeSteps(
  state: AssessmentV2Snapshot,
  results: MatchResult[] = [],
): CauseStepBlock[] {
  const ranked = state.part1.cause_rank_ids ?? [];
  const out: CauseStepBlock[] = [];
  ranked.slice(0, 3).forEach((id, i) => {
    const cause = causeById(id);
    const step: CauseNextStep | null = causeNextStep(id);
    if (!cause || !step) return;
    const fams = new Set(cause.families);
    const match = results.find((r) => fams.has(r.family)) ?? null;
    out.push({
      id,
      label: cause.label,
      ordinal: ORDINAL[i] ?? "next",
      hook: step.hook,
      why: step.why,
      culture: step.culture,
      milestones: step.milestones,
      quickWin: step.quickWin,
      learningPath: causeLearningPath(id),
      firstAction: step.firstAction,
      matchTitle: match?.title ?? null,
    });
  });
  return out;
}


/**
 * One line for the top of the result, naming what we did with their causes
 * and their real habits. Null when there is nothing honest to say.
 */
export function lightsYouUpNote(state: AssessmentV2Snapshot): string | null {
  const ranked = (state.part1.cause_rank_ids ?? [])
    .map((id) => causeById(id)?.label)
    .filter((l): l is string => Boolean(l));
  const live = Object.entries(state.part1.hobby_frequency ?? {})
    .filter(([, f]) => f === "daily" || f === "weekly").length;

  if (ranked.length === 0 && live === 0) return null;

  const parts: string[] = [];
  if (ranked.length > 0) {
    parts.push(
      `You put ${ranked[0]} first${ranked.length > 1 ? `, ahead of ${ranked.slice(1).join(" and ")}` : ""}, so the work below leans that way and the next steps start there.`,
    );
  }
  if (live > 0) {
    parts.push(
      `We also counted the ${live === 1 ? "thing" : `${live} things`} you told us you actually do every week more heavily than the ${live === 1 ? "one" : "ones"} you've drifted from.`,
    );
  }
  return parts.join(" ");
}
